import type { AuthUser, UserPreferences } from "@/types/auth";
import { apiService } from "./apiService";

export const authApi = {
  async getProfile(): Promise<AuthUser> {
    return apiService.get<AuthUser>("/auth/profile");
  },

  async logout(): Promise<void> {
    return apiService.post<void, void>("/auth/logout");
  },

  async updatePreferences(
      preferences: Partial<UserPreferences> & {
        language?: "he" | "en";
        termsVersion?: string;
      },
  ): Promise<AuthUser> {
    return apiService.patch<
        Partial<UserPreferences> & {
      language?: "he" | "en";
      termsVersion?: string;
    },
        AuthUser
    >("/auth/preferences", preferences);
  },

  async register(
      email: string,
      password: string,
      termsVersion: string,
      language?: "he" | "en",
      preferences?: { newsletterConsent?: boolean; marketingConsent?: boolean },
  ): Promise<{
    user: Partial<AuthUser>;
    message: string;
  }> {
    return apiService.post<
        {
          email: string;
          password: string;
          termsVersion: string;
          language?: "he" | "en";
          newsletterConsent?: boolean;
          marketingConsent?: boolean;
        },
        {
          user: Partial<AuthUser>;
          message: string;
        }
    >("/auth/register", {
      email,
      password,
      termsVersion,
      language,
      ...preferences,
    });
  },

  async login(
      email: string,
      password: string,
  ): Promise<{ user: AuthUser; message: string }> {
    return apiService.post<
        { email: string; password: string },
        { user: AuthUser; message: string }
    >("/auth/login", { email, password });
  },

  async checkEmail(
      email: string,
  ): Promise<{ exists: boolean; primaryMethod?: "google" | "password" }> {
    return apiService.post<
        { email: string },
        { exists: boolean; primaryMethod?: "google" | "password" }
    >("/auth/check-email", { email });
  },

  async verifyEmail(
      email: string,
      code: string,
  ): Promise<{ success: boolean; message: string }> {
    return apiService.post<
        { email: string; code: string },
        {
          success: boolean;
          message: string;
        }
    >("/auth/verify-email", { email, code });
  },

  async resendVerification(
      email: string,
  ): Promise<{ success: boolean; message: string }> {
    return apiService.post<
        { email: string },
        { success: boolean; message: string }
    >("/auth/resend-verification", {
      email,
    });
  },

  async forgotPassword(email: string): Promise<{
    success: boolean;
    message: string;
    isOAuthOnly?: boolean;
    authProvider?: "google";
  }> {
    return apiService.post<
        { email: string },
        {
          success: boolean;
          message: string;
          isOAuthOnly?: boolean;
          authProvider?: "google";
        }
    >("/auth/forgot-password", { email });
  },

  async validateResetToken(
      token: string,
  ): Promise<{ valid: boolean; email?: string }> {
    return apiService.post<
        { token: string },
        { valid: boolean; email?: string }
    >("/auth/validate-reset-token", {
      token,
    });
  },

  async resetPassword(
      token: string,
      password: string,
  ): Promise<{
    success: boolean;
    message: string;
    email: string;
  }> {
    return apiService.post<
        { token: string; password: string },
        {
          success: boolean;
          message: string;
          email: string;
        }
    >("/auth/reset-password", { token, password });
  },

  initiateGoogleLogin(): void {
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      throw new Error("VITE_API_URL environment variable is not configured");
    }
    const url = new URL(`${ apiUrl }/auth/google`);
    window.location.href = url.toString();
  },

  async clearUnreadUpdates(): Promise<void> {
    return apiService.put<void, void>("/users/clear-unread-updates");
  },
};
